import { useState, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { IdeologyVector } from '@/types'
import {
  project2D,
  getGridPosition,
  getQuadrantName,
  coordinatesToPixels,
} from '@/lib/ideology2d'
import { POLITICAL_MAP_AXES, POLITICAL_MAP_QUADRANTS } from '@/constants/politicalMap'
import { PoliticalMapTooltip } from './PoliticalMapTooltip'

// ============================================================================
// Types
// ============================================================================

export interface MapEntity {
  id: string
  name: string
  ideology: IdeologyVector
  color?: string
  type: 'party' | 'candidate'
}

interface PoliticalMap2DProps {
  entities: MapEntity[]
  width?: number
  height?: number
  showGridLabels?: boolean      // Show quadrant names in cells
  showSubLabels?: boolean       // Show sub-labels on hover
  showAxisLabels?: boolean      // Show Left/Center/Right and Pluralist/Institutional/Centralized
  onEntityClick?: (id: string) => void
  onEntityHover?: (id: string | null) => void
  highlightedId?: string
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function PoliticalMap2D({
  entities,
  width = 440,
  height = 440,
  showGridLabels = true,
  showSubLabels = true,
  showAxisLabels = true,
  onEntityClick,
  onEntityHover,
  highlightedId,
  className,
}: PoliticalMap2DProps) {
  const [hoveredEntity, setHoveredEntity] = useState<MapEntity | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null)

  // Padding for axis labels
  const labelPadding = showAxisLabels ? 60 : 10
  const topPadding = showAxisLabels ? 30 : 10

  // Calculate the actual map area
  const mapWidth = width - labelPadding - 10
  const mapHeight = height - topPadding - 10
  const mapLeft = labelPadding
  const mapTop = topPadding

  // Grid dimensions (3x3)
  const cellWidth = mapWidth / 3
  const cellHeight = mapHeight / 3

  // Process entities with their positions
  const processedEntities = useMemo(() => {
    return entities.map(entity => {
      const coords = project2D(entity.ideology)
      const pixels = coordinatesToPixels(coords, mapWidth, mapHeight)
      const gridPos = getGridPosition(coords)
      return {
        ...entity,
        coords,
        px: mapLeft + pixels.px,
        py: mapTop + pixels.py,
        gridPos,
        quadrantName: getQuadrantName(gridPos.quadrantX, gridPos.quadrantY)
      }
    })
  }, [entities, mapWidth, mapHeight, mapLeft, mapTop])

  const handleEntityMouseEnter = useCallback((
    entity: MapEntity,
    event: React.MouseEvent
  ) => {
    setHoveredEntity(entity)
    setTooltipPosition({ x: event.clientX, y: event.clientY })
    onEntityHover?.(entity.id)
  }, [onEntityHover])

  const handleEntityMouseLeave = useCallback(() => {
    setHoveredEntity(null)
    setTooltipPosition(null)
    onEntityHover?.(null)
  }, [onEntityHover])

  const handleEntityClick = useCallback((id: string) => {
    onEntityClick?.(id)
  }, [onEntityClick])

  // Handle keyboard navigation
  const handleEntityKeyDown = useCallback((
    event: React.KeyboardEvent,
    id: string
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onEntityClick?.(id)
    }
  }, [onEntityClick])

  return (
    <div className={cn('relative', className)}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible"
        role="img"
        aria-label="2D Political Map showing positions of political entities"
      >
        {/* Background gradient - darker at top (centralized), lighter at bottom (pluralist) */}
        <defs>
          <linearGradient id="mapGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgb(30, 30, 40)" />
            <stop offset="100%" stopColor="rgb(20, 20, 25)" />
          </linearGradient>
        </defs>

        {/* Map background */}
        <rect
          x={mapLeft}
          y={mapTop}
          width={mapWidth}
          height={mapHeight}
          fill="url(#mapGradient)"
          rx={4}
        />

        {/* Grid lines */}
        {[1, 2].map(i => (
          <g key={`grid-${i}`}>
            {/* Vertical lines */}
            <line
              x1={mapLeft + (cellWidth * i)}
              y1={mapTop}
              x2={mapLeft + (cellWidth * i)}
              y2={mapTop + mapHeight}
              stroke="rgb(55, 65, 81)"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
            {/* Horizontal lines */}
            <line
              x1={mapLeft}
              y1={mapTop + (cellHeight * i)}
              x2={mapLeft + mapWidth}
              y2={mapTop + (cellHeight * i)}
              stroke="rgb(55, 65, 81)"
              strokeWidth={1}
              strokeDasharray="4 4"
            />
          </g>
        ))}

        {/* Horizontal axis labels (Top) */}
        {showAxisLabels && POLITICAL_MAP_AXES.horizontal.map((label, i) => (
          <text
            key={`h-label-${i}`}
            x={mapLeft + (cellWidth * i) + (cellWidth / 2)}
            y={mapTop - 10}
            textAnchor="middle"
            className="fill-muted-foreground text-xs font-medium"
          >
            {label}
          </text>
        ))}

        {/* Vertical axis labels (Left) */}
        {showAxisLabels && POLITICAL_MAP_AXES.vertical.map((label, i) => {
          // Vertical axis is inverted: index 0 is bottom (Pluralist), index 2 is top (Centralized)
          const yPosition = mapTop + mapHeight - (cellHeight * i) - (cellHeight / 2)
          return (
            <text
              key={`v-label-${i}`}
              x={mapLeft - 10}
              y={yPosition}
              textAnchor="end"
              dominantBaseline="middle"
              className="fill-muted-foreground text-xs font-medium"
            >
              {label}
            </text>
          )
        })}

        {/* Quadrant names (inside cells) */}
        {showGridLabels && POLITICAL_MAP_QUADRANTS.quadrantNames.map((row, yi) => (
          row.map((name, xi) => {
            // yi 0 = Pluralist (bottom), yi 2 = Centralized (top)
            // In SVG, y increases downward, so we need to invert
            const yIndex = 2 - yi
            const cx = mapLeft + (cellWidth * xi) + (cellWidth / 2)
            const cy = mapTop + (cellHeight * yIndex) + (cellHeight / 2)

            return (
              <text
                key={`quadrant-${yi}-${xi}`}
                x={cx}
                y={cy}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-slate-400/80 text-[10px] font-medium pointer-events-none"
              >
                {name}
              </text>
            )
          })
        ))}

        {/* Entity dots */}
        {processedEntities.map(entity => {
          const isHighlighted = entity.id === highlightedId
          const isHovered = hoveredEntity?.id === entity.id
          const dotSize = entity.type === 'party' ? 10 : 7
          const color = entity.color || (entity.type === 'party' ? '#6366f1' : '#94a3b8')

          return (
            <g
              key={entity.id}
              className="cursor-pointer transition-transform"
              onClick={() => handleEntityClick(entity.id)}
              onMouseEnter={(e) => handleEntityMouseEnter(entity, e)}
              onMouseLeave={handleEntityMouseLeave}
              onKeyDown={(e) => handleEntityKeyDown(e, entity.id)}
              tabIndex={0}
              role="button"
              aria-label={`${entity.name} - ${entity.quadrantName}`}
            >
              {/* Highlight ring */}
              {(isHighlighted || isHovered) && (
                <circle
                  cx={entity.px}
                  cy={entity.py}
                  r={dotSize + 4}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeOpacity={0.5}
                  className="animate-pulse"
                />
              )}

              {/* Shadow/glow */}
              <circle
                cx={entity.px}
                cy={entity.py}
                r={dotSize + 1}
                fill={color}
                fillOpacity={0.3}
              />

              {/* Main dot */}
              <circle
                cx={entity.px}
                cy={entity.py}
                r={dotSize}
                fill={color}
                stroke="rgba(255,255,255,0.8)"
                strokeWidth={2}
                className={cn(
                  'transition-all duration-150',
                  (isHovered || isHighlighted) && 'scale-110'
                )}
                style={{
                  transformOrigin: `${entity.px}px ${entity.py}px`
                }}
              />

              {/* Type indicator for candidates */}
              {entity.type === 'candidate' && (
                <circle
                  cx={entity.px}
                  cy={entity.py}
                  r={2}
                  fill="white"
                />
              )}
            </g>
          )
        })}
      </svg>

      {/* Tooltip */}
      {hoveredEntity && tooltipPosition && showSubLabels && (
        <PoliticalMapTooltip
          entity={hoveredEntity}
          position={tooltipPosition}
        />
      )}
    </div>
  )
}

// ============================================================================
// Legend Component
// ============================================================================

interface PoliticalMapLegendProps {
  entities: MapEntity[]
  onEntityClick?: (id: string) => void
  onEntityHover?: (id: string | null) => void
  highlightedId?: string
  className?: string
}

export function PoliticalMapLegend({
  entities,
  onEntityClick,
  onEntityHover,
  highlightedId,
  className,
}: PoliticalMapLegendProps) {
  return (
    <div className={cn('space-y-1', className)}>
      {entities.map(entity => {
        const color = entity.color || (entity.type === 'party' ? '#6366f1' : '#94a3b8')
        const isHighlighted = entity.id === highlightedId

        return (
          <button
            key={entity.id}
            onClick={() => onEntityClick?.(entity.id)}
            onMouseEnter={() => onEntityHover?.(entity.id)}
            onMouseLeave={() => onEntityHover?.(null)}
            className={cn(
              'flex items-center gap-2 w-full px-2 py-1 rounded text-sm text-left',
              'hover:bg-muted/50 transition-colors',
              isHighlighted && 'bg-muted/50'
            )}
          >
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className={cn(
              'truncate',
              entity.type === 'candidate' && 'text-muted-foreground'
            )}>
              {entity.name}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ============================================================================
// Compact Map Variant
// ============================================================================

interface PoliticalMap2DCompactProps {
  entities: MapEntity[]
  size?: number
  onEntityClick?: (id: string) => void
  className?: string
}

export function PoliticalMap2DCompact({
  entities,
  size = 200,
  onEntityClick,
  className,
}: PoliticalMap2DCompactProps) {
  return (
    <PoliticalMap2D
      entities={entities}
      width={size}
      height={size}
      showGridLabels={false}
      showSubLabels={true}
      showAxisLabels={false}
      onEntityClick={onEntityClick}
      className={className}
    />
  )
}
